import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Search, Loader2, Check } from 'lucide-react';
import { btn, btnPrimary, card, input } from './ui';

// Pick an existing design from the company Design Library (GET /designs → GET /designs/{id} for the image).
export const DesignLibraryPicker = ({ open, onClose, onPick }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open) { setQ(''); setDetail(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const r = await axios.get('/designs', { params: q ? { search: q } : {} }); setRows(r.data || []); }
      catch { toast.error('Could not load Design Library'); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [open, q]);

  const show = async (d) => {
    try { const r = await axios.get(`/designs/${d.id}`); setDetail(r.data); }
    catch { toast.error('Could not load design'); }
  };

  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }} data-testid="design-library-picker">
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 820, maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0, fontWeight: 800, flex: 1 }}>Design Library</h3>
          <button type="button" onClick={onClose} style={btn} data-testid="design-picker-close"><X size={14} /></button>
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#71717A' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by design name, customer or notes" style={{ ...input, paddingLeft: 30 }} data-testid="design-picker-search" />
        </div>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: detail ? 'minmax(0,1fr) minmax(0,1fr)' : '1fr', overflow: 'hidden', minHeight: 0 }}>
          <div style={{ overflowY: 'auto', maxHeight: '58vh', display: 'grid', gap: 6, alignContent: 'start' }}>
            {loading && <div style={{ color: '#A1A1AA', fontSize: 13 }}><Loader2 size={13} className="animate-spin" /> Loading…</div>}
            {!loading && rows.length === 0 && <div style={{ color: '#71717A', fontSize: 13 }} data-testid="design-picker-empty">No designs found. Upload artwork in Design Library first, or use “Upload artwork” here.</div>}
            {rows.map((d) => (
              <button type="button" key={d.id} onClick={() => show(d)} data-testid={`design-row-${d.id}`}
                style={{ ...btn, justifyContent: 'flex-start', textAlign: 'left', background: detail?.id === d.id ? '#312E81' : '#18181B', display: 'block' }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{d.name}</div>
                <div style={{ fontSize: 11, color: '#A1A1AA' }}>{[d.customer_name, d.print_method, d.decoration_notes].filter(Boolean).join(' · ') || '—'}</div>
              </button>
            ))}
          </div>
          {detail && (
            <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
              <div style={{ background: 'repeating-conic-gradient(#27272A 0 25%, #18181B 0 50%) 0 0/16px 16px', borderRadius: 8, padding: 8, display: 'grid', placeItems: 'center', minHeight: 160 }}>
                <img src={detail.image_base64} alt={detail.name} style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain' }} data-testid="design-picker-preview" />
              </div>
              <div style={{ fontWeight: 700 }}>{detail.name}</div>
              <button type="button" style={btnPrimary} onClick={() => { onPick(detail); onClose(); }} data-testid="design-picker-use"><Check size={14} /> Use this design</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
